/* eslint-disable no-nested-ternary */
/* eslint-disable no-return-assign */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Container,
  Typography,
  Stack,
  Button,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { LocalizationProvider, DateRangePicker } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import ReactToPrint from 'react-to-print';
import { toast } from 'react-toastify';
import {
  FileText,
  Wallet,
  Users,
  UserCog,
  ShoppingCart,
  Percent,
  Repeat,
  Eye,
  EyeOff,
  ChartArea,
  Bell,
  ArrowUpRight,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';
import moment from 'moment-timezone';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

// Internal API/Store Imports
import { fCurrency } from '../utils/formatNumber';
import {
  generateAdminTransactionReport,
  getTransactionsHistory,
  generateProfitAndLossReport,
} from '../api/transactions.api';
import { getSubscriptions } from '../api/subscriptions.api';
import { getServices } from '../store/actions/services';
import { getStaffMembers, getUsers, getUsersWallet, fetchDashboardOverview } from '../store/actions/users';
import { hasPermission, PERMISSIONS } from '../utils/hasPermission';

import Page from '../components/Page';
import ModalC from './components/CModal';
import ProcessorReportTable from './components/ProcessorTable';
import { ComponentToPrint } from './components/PrintComponent';

// ─── Animations & Styled Components ──────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const GlassCard = styled(Paper)(({ theme }) => ({
  borderRadius: 24,
  padding: theme.spacing(3),
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: '10px 20px',
  textTransform: 'none',
  fontWeight: 600,
  fontFamily: '"SF Pro Text", sans-serif',
}));

// ─── Dummy Data for Charts ───────────────────────────────────────────────────

const DUMMY_REVENUE_DATA = [
  { name: 'Mon', amount: 4000 },
  { name: 'Tue', amount: 3000 },
  { name: 'Wed', amount: 5000 },
  { name: 'Thu', amount: 2780 },
  { name: 'Fri', amount: 1890 },
  { name: 'Sat', amount: 2390 },
  { name: 'Sun', amount: 3490 },
];

const DUMMY_SERVICE_DATA = [
  { name: 'Data', value: 400, color: '#0071e3' },
  { name: 'Airtime', value: 300, color: '#34c759' },
  { name: 'Electricity', value: 100, color: '#ff9500' },
  { name: 'Cable', value: 200, color: '#af52de' },
];

// ─── Sub-Components ──────────────────────────────────────────────────────────

function StatWidget({ title, total, icon: Icon, color, delay = 0, onClick, showToggle, isVisible }) {
  return (
    <GlassCard
      sx={{
        animation: `${fadeUp} 0.6s ease backwards ${delay}ms`,
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
      }}
      onClick={onClick}
    >
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box
            sx={{
              p: 1.5,
              borderRadius: '14px',
              bgcolor: `${color}15`,
              color,
              display: 'flex',
            }}
          >
            <Icon size={24} />
          </Box>
          {showToggle && (
            <IconButton size="small" sx={{ color: '#86868b' }}>
              {isVisible ? <EyeOff size={18} /> : <Eye size={18} />}
            </IconButton>
          )}
        </Stack>
        <Box>
          <Typography
            variant="caption"
            sx={{ color: '#86868b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}
          >
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: '#1d1d1f', wordBreak: 'break-word' }}>
            {total}
          </Typography>
        </Box>
      </Stack>
    </GlassCard>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function DashboardApp() {
  const theme = useTheme();
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { user, activeApp: reduxActiveApp } = useSelector((state) => state.auth);
  const { staffMembers, overview: overviewData } = useSelector((state) => state.users);

  const [value, setValue] = useState([null, null]);
  const [loading, setLoading] = useState(false);
  const [profitLoading, setProfitLoading] = useState(false);
  const [showGenerateReportModal, setShowGenerateReportModal] = useState(false);
  const [statisticResult, setStatisticResult] = useState(null);
  const [profitResult, setProfitResult] = useState(null);
  const [showAmount, setShowAmount] = useState(false);
  const [expiredSub, setExpiredSub] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionLoading, setTransactionLoading] = useState(false);

  const getTransactions = useCallback(async () => {
    setTransactionLoading(true);
    const filterOptions = { page: 1, filterTransactionType: '', searchQuery: '', isManual: false };
    try {
      const response = await getTransactionsHistory(filterOptions);
      if (response.data) setTransactions(response.data.transactions);
    } catch (error) {
      console.error(error);
    }
    setTransactionLoading(false);
  }, []);

  useEffect(() => {
    const fetchHostingSub = async () => {
      let activeApp = reduxActiveApp;
      if (!activeApp) {
        try {
          const bootstrapped = sessionStorage.getItem('bootstrappedApp');
          if (bootstrapped) {
            activeApp = JSON.parse(bootstrapped);
          } else {
            const storedActive = localStorage.getItem('activeApp');
            if (storedActive) {
              activeApp = JSON.parse(storedActive);
            }
          }
        } catch (e) {
          // ignore
        }
      }
      if (!activeApp) return;
      try {
        const res = await getSubscriptions();
        if (res.ok && res.data?.success) {
          const appSubs = (res.data.data || []).filter(
            (sub) => sub.app?._id === activeApp._id || sub.app === activeApp._id
          );
          const hostingSub = appSubs.find(
            (sub) => sub.product?.type === 'hosting' && sub.daysLeft <= 7
          );
          setExpiredSub(hostingSub || null);
        }
      } catch (err) {
        console.error('Error fetching subscription details:', err);
      }
    };

    dispatch(getServices());
    dispatch(getStaffMembers());
    getTransactions();
    fetchHostingSub();
    if (hasPermission(user, PERMISSIONS.DASHBOARD_ANALYTICS)) {
      dispatch(fetchDashboardOverview());
    }
  }, [dispatch, getTransactions, user]);

  // Helpers
  const formatDate = (date) => (date ? moment(date).format('YYYY-MM-DD') : '');

  const handleGenerateAdminReport = async () => {
    if (!value[0] || !value[1]) return toast.error('Select dates');
    setLoading(true);
    const request = await generateAdminTransactionReport({
      startDate: formatDate(value[0]),
      endDate: formatDate(value[1]),
    });
    if (request.ok) setStatisticResult(request?.data?.data);
    setLoading(false);
  };

  const handleCalculateProfit = async () => {
    setProfitLoading(true);
    const request = await generateProfitAndLossReport({
      startDate: formatDate(value[0]),
      endDate: formatDate(value[1]),
    });
    if (request.ok) setProfitResult(request?.data?.data);
    setProfitLoading(false);
  };

  return (
    <Page title="Dashboard">
      <Box sx={{ minHeight: '100vh', bgcolor: '#fbfbfd', pb: 8 }}>
        <Container maxWidth="xl">
          {expiredSub && (
            <Box
              sx={{
                bgcolor: '#fff0f0',
                color: '#c92a2a',
                border: '1px solid #ffe3e3',
                px: 4,
                py: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 4,
                borderRadius: 3,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <AlertTriangle color="#c92a2a" size={20} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Your {expiredSub.product.name} subscription has expired or is expiring soon!
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#e03131' }}>
                    You have <strong>{expiredSub.daysLeft} days</strong> to renew it and avoid interruptions to your
                    services.
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => navigation('/dashboard/billing')}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '20px', px: 3 }}
              >
                Renew
              </Button>
            </Box>
          )}
          {/* Header Section */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            sx={{ pt: 5, mb: 4 }}
            spacing={2}
          >
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.04em', color: '#1d1d1f' }}>
                Dashboard
              </Typography>
              <Typography variant="body1" sx={{ color: '#86868b' }}>
                Welcome back, {user?.firstName || 'Admin'}. Here is your business overview.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ width: { xs: '100%', md: 'auto' } }}>
              <Tooltip title="Notifications">
                <IconButton
                  onClick={() => navigation('/dashboard/user/notifications')}
                  sx={{ bgcolor: '#fff', border: '1px solid #d2d2d7' }}
                >
                  <Bell size={20} color="#424245" />
                </IconButton>
              </Tooltip>
              <ActionButton
                variant="contained"
                startIcon={<FileText size={18} />}
                onClick={() => setShowGenerateReportModal(true)}
                sx={{ bgcolor: '#0071e3', '&:hover': { bgcolor: '#0077ed' }, flex: { xs: 1, md: 'none' } }}
              >
                Generate Report
              </ActionButton>
            </Stack>
          </Stack>

          {/* Bento Grid Stats */}
          <Grid container spacing={3} sx={{ mb: 4 }} alignItems="stretch">
            {hasPermission(user, [PERMISSIONS.DASHBOARD_ANALYTICS, PERMISSIONS.TRANSACTIONS_READ]) && (
              <Grid item xs={12} sm={6} md={3}>
                <StatWidget
                  title="Total Wallet"
                  total={showAmount ? `₦${fCurrency(overviewData?.totalWalletBalance ?? 0)}` : '••••••'}
                  icon={Wallet}
                  color="#0071e3"
                  onClick={() => setShowAmount(!showAmount)}
                  showToggle
                  isVisible={showAmount}
                />
              </Grid>
            )}
            {hasPermission(user, [PERMISSIONS.DASHBOARD_ANALYTICS, PERMISSIONS.TRANSACTIONS_READ]) && (
              <Grid item xs={12} sm={6} md={3}>
                <StatWidget
                  title="Commissions"
                  total={showAmount ? `₦${fCurrency(overviewData?.totalCommissionBalance ?? 0)}` : '••••••'}
                  icon={Percent}
                  color="#34c759"
                  onClick={() => setShowAmount(!showAmount)}
                  showToggle
                  isVisible={showAmount}
                  delay={100}
                />
              </Grid>
            )}
            {hasPermission(user, [PERMISSIONS.DASHBOARD_ANALYTICS, PERMISSIONS.AGENTS_READ]) && (
              <Grid item xs={12} sm={6} md={3}>
                <StatWidget
                  title="Active Users"
                  total={overviewData?.totalUsers ?? 0}
                  icon={Users}
                  color="#af52de"
                  onClick={() => navigation('/dashboard/user-management')}
                  delay={200}
                />
              </Grid>
            )}
            {hasPermission(user, [PERMISSIONS.DASHBOARD_ANALYTICS, PERMISSIONS.STAFF_READ]) && (
              <Grid item xs={12} sm={6} md={3}>
                <StatWidget
                  title="Staff"
                  total={overviewData?.totalStaff ?? staffMembers?.length ?? 0}
                  icon={UserCog}
                  color="#ff9500"
                  onClick={() => navigation('/dashboard/staff')}
                  delay={300}
                />
              </Grid>
            )}
          </Grid>

          {/* Quick Action Navigation Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }} alignItems="stretch">
            {[
              {
                title: 'Services',
                desc: 'Manage your offerings',
                icon: ShoppingCart,
                color: '#0071e3',
                path: '/dashboard/services',
                permission: PERMISSIONS.SERVICES_READ,
              },
              {
                title: 'Transactions',
                desc: 'View complete history',
                icon: Repeat,
                color: '#34c759',
                path: '/dashboard/transaction',
                permission: PERMISSIONS.TRANSACTIONS_READ,
              },
              {
                title: 'Analytics',
                desc: 'Detailed business insights',
                icon: ChartArea,
                color: '#af52de',
                path: '/dashboard/analytics',
                permission: PERMISSIONS.TRANSACTIONS_ANALYTICS,
              },
            ]
              .filter((action) => !action.permission || hasPermission(user, action.permission))
              .map((action, index) => (
                <Grid item xs={12} md={4} key={action.title}>
                  <GlassCard
                    onClick={() => navigation(action.path)}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      height: '100%',
                      animation: `${fadeUp} 0.6s ease backwards ${index * 150 + 200}ms`,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        sx={{ p: 2, borderRadius: 3, bgcolor: `${action.color}15`, color: action.color, flexShrink: 0 }}
                      >
                        <action.icon size={26} strokeWidth={2} />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1d1d1f', lineHeight: 1.2 }}>
                          {action.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#86868b', mt: 0.5 }}>
                          {action.desc}
                        </Typography>
                      </Box>
                    </Stack>
                    <ChevronRight size={20} color="#86868b" style={{ flexShrink: 0 }} />
                  </GlassCard>
                </Grid>
              ))}
          </Grid>

          {/* Charts & Analytics Row - FIX: Converted fixed heights to Flex column layouts */}
          <Grid container spacing={3} sx={{ mb: 4 }} alignItems="stretch">
            <Grid item xs={12} lg={8}>
              <GlassCard sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Revenue Trend
                  </Typography>
                  <Chip label="+12.5% this week" color="success" size="small" variant="soft" />
                </Stack>
                {/* flexGrow ensures the chart takes remaining space, minHeight guarantees it doesn't collapse */}
                <Box sx={{ flexGrow: 1, minHeight: 300, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={DUMMY_REVENUE_DATA}>
                      <defs>
                        <linearGradient id="colorAmt" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0071e3" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#0071e3" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#86868b', fontSize: 12 }}
                      />
                      <YAxis hide />
                      <RechartsTooltip />
                      <Area
                        type="monotone"
                        dataKey="amount"
                        stroke="#0071e3"
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#colorAmt)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </GlassCard>
            </Grid>

            <Grid item xs={12} lg={4}>
              <GlassCard sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                  Service Distribution
                </Typography>

                {/* Flexbox dynamically handles the chart size while respecting the stack below */}
                <Box sx={{ flexGrow: 1, minHeight: 220, width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={DUMMY_SERVICE_DATA} innerRadius={65} outerRadius={85} paddingAngle={5} dataKey="value">
                        {DUMMY_SERVICE_DATA.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                <Stack spacing={1.5} mt={3}>
                  {DUMMY_SERVICE_DATA.map((item) => (
                    <Stack key={item.name} direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color }} />
                        <Typography variant="body2" sx={{ color: '#424245', fontWeight: 500 }}>
                          {item.name}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {item.value} txns
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </GlassCard>
            </Grid>
          </Grid>

          {/* Recent Transactions Table Component */}
          <GlassCard sx={{ p: 0, overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Recent Transactions
              </Typography>
              <Button
                endIcon={<ArrowUpRight size={16} />}
                onClick={() => navigation('/dashboard/transaction')}
                sx={{ color: '#0071e3', textTransform: 'none' }}
              >
                View History
              </Button>
            </Stack>

            <Box sx={{ overflowX: 'auto' }}>
              <Stack spacing={0.5} sx={{ px: 2, pb: 3, minWidth: 400 }}>
                {transactionLoading ? (
                  <Stack alignItems="center" py={5}>
                    <CircularProgress size={30} />
                  </Stack>
                ) : transactions?.length > 0 ? (
                  transactions.slice(0, 6).map((txn, idx) => (
                    <Stack
                      key={txn.id || idx}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        '&:hover': { bgcolor: '#f5f5f7' },
                        transition: '0.2s',
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: txn.status === 'success' ? '#e8f5e9' : '#fff4e5',
                            borderRadius: 2,
                            color: txn.status === 'success' ? '#2e7d32' : '#ed6c02',
                            flexShrink: 0,
                          }}
                        >
                          <Repeat size={20} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>{txn.name}</Typography>
                          <Typography variant="caption" sx={{ color: '#86868b' }}>
                            {moment(txn.createdAt).format('MMM DD, hh:mm A')}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack alignItems="flex-end">
                        <Typography sx={{ fontWeight: 700 }}>₦{fCurrency(txn.amount)}</Typography>
                        <Chip
                          label={txn.status}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            bgcolor: txn.status === 'success' ? '#34c75920' : '#ff3b3020',
                            color: txn.status === 'success' ? '#34c759' : '#ff3b30',
                          }}
                        />
                      </Stack>
                    </Stack>
                  ))
                ) : (
                  <Typography sx={{ p: 4, textAlign: 'center', color: '#86868b' }}>No transactions found</Typography>
                )}
              </Stack>
            </Box>
          </GlassCard>
        </Container>

        {/* ─── MODALS ─── */}

        {/* Generate Report Modal */}
        <ModalC
          isOpen={showGenerateReportModal}
          setOpen={() => setShowGenerateReportModal(false)}
          sx={{ width: { xs: '95vw', sm: 600 }, p: 4 }}
        >
          <Stack spacing={3}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Business Analytics Report
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateRangePicker
                value={value}
                onChange={(newValue) => setValue(newValue)}
                renderInput={(start, end) => (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <TextField {...start} fullWidth size="small" />
                    <Typography>to</Typography>
                    <TextField {...end} fullWidth size="small" />
                  </Stack>
                )}
              />
            </LocalizationProvider>

            {loading ? (
              <Stack alignItems="center" py={4}>
                <CircularProgress />
              </Stack>
            ) : (
              statisticResult && (
                <Box sx={{ bgcolor: '#f5f5f7', p: 2, borderRadius: 3, maxHeight: 300, overflowY: 'auto' }}>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    Summary for {formatDate(value[0])}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption">Money In</Typography>
                      <Typography variant="h6">₦{fCurrency(statisticResult.moneyIn)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption">Money Out</Typography>
                      <Typography variant="h6">₦{fCurrency(statisticResult.moneyOut)}</Typography>
                    </Grid>
                  </Grid>
                  {/* Add more report mapping here based on original code */}
                </Box>
              )
            )}

            <Stack direction="row" spacing={2}>
              <Button fullWidth variant="outlined" onClick={() => setShowGenerateReportModal(false)}>
                Close
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleGenerateAdminReport}
                disabled={loading}
                sx={{ bgcolor: '#1d1d1f' }}
              >
                Fetch Data
              </Button>
            </Stack>

            {statisticResult && (
              <Button variant="contained" color="success" onClick={handleCalculateProfit} disabled={profitLoading}>
                {profitLoading ? 'Calculating...' : 'Calculate Profit'}
              </Button>
            )}
          </Stack>
        </ModalC>
      </Box>
    </Page>
  );
}
